import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CreditCard, Settings, Download, AlertTriangle, CheckCircle, Crown, Clock, X, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { getFreshToken } from '../lib/firebase';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const BillingPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [billing, setBilling] = useState(null);
  const [stripeSub, setStripeSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  // Success banner on ?success=true
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      setShowSuccess(true);
      window.history.replaceState({}, '', '/billing');
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getFreshToken();
      if (!token) { setLoading(false); setInvoicesLoading(false); return; }
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [billingRes, subRes, invoicesRes] = await Promise.all([
          fetch(`${BACKEND}/api/account/billing`, { headers }),
          fetch(`${BACKEND}/api/stripe/subscription`, { headers }),
          fetch(`${BACKEND}/api/stripe/invoices`, { headers }),
        ]);
        if (billingRes.ok) setBilling(await billingRes.json());
        if (subRes.ok) setStripeSub(await subRes.json());
        if (invoicesRes.ok) {
          const data = await invoicesRes.json();
          setInvoices(data.invoices || []);
        }
      } catch (e) {
        console.error('Failed to fetch billing:', e);
      } finally {
        setLoading(false);
        setInvoicesLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const nextBillingDate = stripeSub?.current_period_end
    ? new Date(stripeSub.current_period_end * 1000)
    : null;

  const isSubscribed = !!billing?.plan;
  const currentPlan = billing?.plan;
  const foundingSince = billing?.founding_member_since;

  const availablePlans = [
    {
      name: 'Wanderloop+ Monthly',
      tier: 'wanderloop_plus_monthly',
      price: 9.99,
      billing_cycle: 'monthly',
      description: 'Billed monthly — $120/year',
    },
    {
      name: 'Wanderloop+ Annual',
      tier: 'wanderloop_plus_annual',
      price: 29,
      billing_cycle: 'annual',
      description: 'Founding member rate — locked in forever',
      badge: 'Founding Rate',
    },
  ];

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatFoundingDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  const formatInvoiceDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'usd',
    }).format(amount / 100);
  };

  const openCustomerPortal = async () => {
    const token = await getFreshToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND}/api/stripe/customer-portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error('Failed to open portal:', e);
    }
  };

  const downloadInvoice = async () => {
    const token = await getFreshToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND}/api/stripe/latest-invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.invoice_pdf) {
        window.open(data.invoice_pdf, '_blank');
      } else if (data.hosted_url) {
        window.open(data.hosted_url, '_blank');
      }
    } catch (e) {
      console.error('Failed to download invoice:', e);
    }
  };

  const handleSubscribe = () => {
    window.location.href = '/membership';
  };

  const handlePlanSwitch = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to switch plans? This will take effect at the end of your current billing period.'
    );
    if (!confirmed) return;
    await openCustomerPortal();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Header />

      {/* Success Banner */}
      {showSuccess && (
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black py-4 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6" />
              <span className="font-bold text-lg">Welcome to Wanderloop+, founding member!</span>
            </div>
            <button onClick={() => setShowSuccess(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Subscription</h1>
          <p className="text-gray-600">Manage your subscription and payment information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Plan */}
            <Card className={`border-l-4 ${isSubscribed ? 'border-l-amber-500' : 'border-l-gray-300'}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-500" />
                  Current Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isSubscribed ? (<>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      {currentPlan.name}
                      <Badge className="bg-amber-100 text-amber-800">
                        {currentPlan.status}
                      </Badge>
                    </h3>
                    <p className="text-gray-600 capitalize">
                      {currentPlan.billing_cycle} billing
                    </p>
                    <p className="text-sm text-amber-700 font-medium">
                      Founding Member Rate
                    </p>
                    {foundingSince && (
                      <p className="text-sm text-gray-500">
                        Founding Member Since {formatFoundingDate(foundingSince)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      ${currentPlan.amount}
                    </div>
                    <div className="text-sm text-gray-500">
                      per {currentPlan.billing_cycle === 'annual' ? 'year' : 'month'}
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 italic mb-4">
                  If you cancel, founding member pricing cannot be guaranteed on resubscription.
                </p>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
                  {nextBillingDate ? (
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Next billing: {formatDate(nextBillingDate)}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>Next billing date will appear after your first payment</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex items-center gap-2" onClick={openCustomerPortal}>
                    <Settings className="h-4 w-4" />
                    Change Plan
                  </Button>
                  <Button variant="outline" className="flex items-center gap-2" onClick={downloadInvoice}>
                    <Download className="h-4 w-4" />
                    Download Invoice
                  </Button>
                </div>
                </>) : (
                  <div className="py-2">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        Free
                        <Badge className="bg-gray-100 text-gray-700">Not subscribed</Badge>
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        You're not currently subscribed to Wanderloop+.
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Subscribe to unlock founding-member rate, premium walks, journals, and offline access.
                    </p>
                    <Button onClick={handleSubscribe} className="bg-amber-500 hover:bg-amber-600 text-white">
                      <Crown className="h-4 w-4 mr-2" />
                      Subscribe to Wanderloop+
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoicesLoading ? (
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">No payments yet</p>
                    <p className="text-sm text-gray-500">
                      Your payment history will appear here after your first billing cycle.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                            <Calendar className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">
                              {inv.description || 'Wanderloop+'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatInvoiceDate(inv.created)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-bold text-gray-900 text-sm">
                              {formatAmount(inv.amount, inv.currency)}
                            </div>
                            <Badge className={`text-xs ${
                              inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                              inv.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {inv.status}
                            </Badge>
                          </div>
                          {inv.invoice_pdf && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(inv.invoice_pdf, '_blank')}
                              className="flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" />
                              PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Method (subscribers only) */}
            {isSubscribed && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        Manage in Stripe
                      </div>
                      <div className="text-sm text-gray-500">
                        Update card, view details
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={openCustomerPortal}>
                    Update
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Available Plans */}
            <Card>
              <CardHeader>
                <CardTitle>Available Plans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {availablePlans.map((plan) => (
                    <div
                      key={plan.tier}
                      className={`p-4 border rounded-lg transition-all duration-200 ${
                        isSubscribed && plan.tier === currentPlan?.tier
                          ? 'border-amber-400 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          {plan.name}
                          {plan.badge && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">
                              {plan.badge}
                            </Badge>
                          )}
                        </h4>
                        <div className="text-lg font-bold text-gray-900">
                          ${plan.price}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {plan.description}
                      </p>

                      {isSubscribed && plan.tier === currentPlan?.tier ? (
                        <Badge className="bg-amber-100 text-amber-800">
                          Current Plan
                        </Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={isSubscribed ? handlePlanSwitch : handleSubscribe}
                        >
                          {isSubscribed ? 'Switch' : 'Subscribe'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Billing Alerts (subscribers only) */}
            {isSubscribed && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Billing Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Payment up to date
                      </div>
                      <div className="text-xs text-gray-500">
                        Your subscription is active and billing is current
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Auto-renewal enabled
                      </div>
                      <div className="text-xs text-gray-500">
                        Your subscription will renew automatically
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BillingPage;
